import { useCallback, useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from "react";

import { calculateBoxArea, calculateSelectionBox } from "../utils";

const MIN_SELECTION_AREA = 10;

interface RectanglePoint {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

interface RectangleBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

interface SelectableItemEntry {
  id: string;
  element: HTMLElement;
  disabled?: boolean;
}

interface UseRectangleSelectionProps {
  enabled: boolean;
  container: HTMLElement | null;
  getSelectableItems: () => SelectableItemEntry[];
  onRectangleSelect?: (itemIds: string[]) => void;
  onDraggingChange?: (isDragging: boolean) => void;
}

const normalizeRectangle = ({ startX, startY, endX, endY }: RectanglePoint): RectangleBounds => ({
  left: Math.min(startX, endX),
  top: Math.min(startY, endY),
  right: Math.max(startX, endX),
  bottom: Math.max(startY, endY),
});

const isSignificantSelectionRect = (rectangle: RectanglePoint): boolean => {
  const box = calculateSelectionBox({
    startPoint: { x: rectangle.startX, y: rectangle.startY },
    endPoint: { x: rectangle.endX, y: rectangle.endY },
  });
  return calculateBoxArea(box) >= MIN_SELECTION_AREA;
};

const isItemInSelectionRect = (itemRect: DOMRect, containerRect: DOMRect, selectionRect: RectangleBounds): boolean => {
  const itemLeft = itemRect.left - containerRect.left;
  const itemRight = itemRect.right - containerRect.left;
  const itemTop = itemRect.top - containerRect.top;
  const itemBottom = itemRect.bottom - containerRect.top;

  const horizontalOverlap = itemRight >= selectionRect.left && itemLeft <= selectionRect.right;
  const verticalOverlap = itemBottom >= selectionRect.top && itemTop <= selectionRect.bottom;

  return horizontalOverlap && verticalOverlap;
};

const getItemsInSelectionRect = (
  items: SelectableItemEntry[],
  containerRect: DOMRect,
  selectionRect: RectangleBounds,
): string[] =>
  items
    .filter(
      item =>
        !item.disabled && isItemInSelectionRect(item.element.getBoundingClientRect(), containerRect, selectionRect),
    )
    .map(item => item.id);

const useRectangleSelection = (props: UseRectangleSelectionProps) => {
  const { enabled, container, getSelectableItems, onRectangleSelect, onDraggingChange } = props;

  const [isDragging, setIsDragging] = useState(false);
  const [rectangle, setRectangle] = useState<RectanglePoint>({ startX: 0, startY: 0, endX: 0, endY: 0 });

  const rectangleRef = useRef(rectangle);
  rectangleRef.current = rectangle;

  const isDraggingRef = useRef(isDragging);
  isDraggingRef.current = isDragging;

  const getRelativePoint = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } | null => {
      if (!container) return null;
      const bounds = container.getBoundingClientRect();
      return {
        x: clientX - bounds.left,
        y: clientY - bounds.top,
      };
    },
    [container],
  );

  const selectItemsInRect = useCallback(() => {
    if (!container || !onRectangleSelect) return;

    const currentRectangle = rectangleRef.current;
    if (!isSignificantSelectionRect(currentRectangle)) return;

    const containerRect = container.getBoundingClientRect();
    const selectionRect = normalizeRectangle(currentRectangle);
    const itemIds = getItemsInSelectionRect(getSelectableItems(), containerRect, selectionRect);

    if (itemIds.length > 0) {
      onRectangleSelect(itemIds);
    }
  }, [container, getSelectableItems, onRectangleSelect]);

  const stopDragging = useCallback(() => {
    setIsDragging(false);
    onDraggingChange?.(false);
  }, [onDraggingChange]);

  const handleMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (!enabled || !event.shiftKey) return;

      const point = getRelativePoint(event.clientX, event.clientY);
      if (!point) return;

      setIsDragging(true);
      onDraggingChange?.(true);
      setRectangle({
        startX: point.x,
        startY: point.y,
        endX: point.x,
        endY: point.y,
      });
    },
    [enabled, getRelativePoint, onDraggingChange],
  );

  const handleMouseMove = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (!isDragging || !enabled) return;

      const point = getRelativePoint(event.clientX, event.clientY);
      if (!point) return;

      setRectangle(prevState => ({
        startX: prevState.startX,
        startY: prevState.startY,
        endX: point.x,
        endY: point.y,
      }));
    },
    [enabled, getRelativePoint, isDragging],
  );

  const handleMouseUp = useCallback(() => {
    if (!isDraggingRef.current || !enabled) return;

    selectItemsInRect();
    stopDragging();
  }, [enabled, selectItemsInRect, stopDragging]);

  useEffect(() => {
    if (!isDragging) return;

    const handleWindowMouseMove = (event: MouseEvent) => {
      const point = getRelativePoint(event.clientX, event.clientY);
      if (!point) return;

      setRectangle(prevState => ({
        startX: prevState.startX,
        startY: prevState.startY,
        endX: point.x,
        endY: point.y,
      }));
    };

    const handleWindowMouseUp = () => {
      selectItemsInRect();
      stopDragging();
    };

    window.addEventListener("mousemove", handleWindowMouseMove);
    window.addEventListener("mouseup", handleWindowMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleWindowMouseMove);
      window.removeEventListener("mouseup", handleWindowMouseUp);
    };
  }, [getRelativePoint, isDragging, selectItemsInRect, stopDragging]);

  return {
    isDragging,
    rectangle,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  };
};

export {
  getItemsInSelectionRect,
  isItemInSelectionRect,
  isSignificantSelectionRect,
  normalizeRectangle,
  useRectangleSelection,
};
export type { RectangleBounds, SelectableItemEntry, UseRectangleSelectionProps };
